import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import TextareaAutosize from 'react-textarea-autosize'
import { Button, Form } from 'semantic-ui-react'

import { SimpleButton } from 'ui'

import { IMPORT_TYPES_NAMES, STAGES_TYPES_NAMES } from 'tournaments/src/constants'

import { actions, selectors } from '../../redux'
import { StageTypes } from '../../schemas/StageSchema'

import type { FC } from 'react'

import type { ImportTypes, StageSchema } from '../../schemas/StageSchema'

export interface EditProps {
  maxOrder: number
  onCancel: () => void
  onSave: () => void
  tournamentId: string

  stage?: StageSchema
}

const renderOptionsList = ([value, text]: [string, string]) => (
  <option key={value} value={value}>
    {text}
  </option>
)

const Edit: FC<EditProps> = ({ maxOrder = 1, onCancel, onSave, stage, tournamentId }) => {
  const dispatch = useDispatch()
  const { error, loaded, loading } = useSelector(selectors.stages.stage)
  const [isSaved, setIsSaved] = useState(false)

  useEffect(() => {
    if (isSaved && loaded) {
      setIsSaved(false)
      if (!error) {
        onSave()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaved, loaded])

  const { formState, getValues, handleSubmit, register, setValue } = useForm({
    defaultValues: {
      importType: stage?.imports?.[0]?.type || '',
      importUrl: stage?.imports?.[0]?.url || '',
      name: stage?.name || '',
      order: stage?.order === undefined ? maxOrder : (Number(stage.order) || 0) + 1,
      stageEnded: !!stage?.is_ended,
      type: stage?.type || StageTypes.TYPE_GROUPS,
    },
  })

  const onSubmit = (values: {
    importType: ImportTypes | string
    importUrl: string
    name: string
    order: number
    stageEnded: boolean
    type: StageTypes
  }) => {
    dispatch(actions.stageSave({ ...values, id: stage?.id, tournament_id: tournamentId }))
    setIsSaved(true)
  }

  return (
    <div className="bg-white p-2">
      <h3>{stage?.id ? 'Editar' : 'Crear'} fase</h3>
      <Form className="flex flex-col gap-2" noValidate onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-wrap gap-2">
          <div className="grow min-w-52">
            <Form.Input error={formState.errors.name?.message} label="Nombre" readOnly={loading} required>
              <input
                {...register('name', {
                  maxLength: {
                    message: 'Máximo 100 caracteres',
                    value: 100,
                  },
                  required: 'El nombre es requerido',
                })}
                maxLength={100}
                type="text"
              />
            </Form.Input>
            <Form.Input error={formState.errors.order?.message} label="Posición" readOnly={loading} required>
              <input
                {...register('order', {
                  max: {
                    message: `El máximo permitido es ${maxOrder}`,
                    value: maxOrder,
                  },
                  min: {
                    message: 'El mínimo permitido es 1',
                    value: 1,
                  },
                  required: 'la posición es requerido',
                })}
                max={maxOrder}
                min={1}
                type="number"
              />
            </Form.Input>
            <Form.Input error={formState.errors.type?.message} label="Tipo" readOnly={loading} required>
              <select
                {...register('type', {
                  required: 'la posición es requerido',
                })}
              >
                {Object.entries(STAGES_TYPES_NAMES).map(renderOptionsList)}
              </select>
            </Form.Input>
            <Form.Input error={formState.errors.importType?.message} label="Fuente de conexión" readOnly={loading}>
              <select {...register('importType')}>
                <option value=""> Sin Conexión</option>
                {Object.entries(IMPORT_TYPES_NAMES).map(renderOptionsList)}
              </select>
            </Form.Input>
            <Form.Input
              className="!mb-0"
              error={formState.errors.importUrl?.message}
              label="Llave de conexión"
              readOnly={loading}
            >
              <TextareaAutosize {...register('importUrl')} />
            </Form.Input>
            <SimpleButton
              className="mb-2"
              disabled={loading}
              icon="recycle"
              onClick={() => {
                const url = getValues('importUrl')
                const matches = url
                  .split('\n')
                  .filter((s) => s.match(/\S([0-9]{7,})\S/))
                  .map((s) => {
                    const code = s.split('\t').find((t) => t.match(/^[0-9]{7,}$/))
                    const group = s.split('\t').find((t) => t.match(/.*(group|grupo)[ \-_"']+([a-z0-9]+).*/i)) || ''
                    return `${code}${group ? `-${group.replace(/.*(group|grupo)[ \-_"']+([a-z0-9]+).*/i, '$2')}` : ''}`
                  })

                setValue('importUrl', matches.join(', '))
              }}
              primary
            >
              Limpiar
            </SimpleButton>
            <Form.Input error={formState.errors.stageEnded?.message} label="Estado de la fase" readOnly={loading}>
              <label>
                <span>
                  <input className="!w-10 !max-w-10" {...register('stageEnded')} type="checkbox" />
                </span>{' '}
                Fase terminada
              </label>
            </Form.Input>
          </div>
        </div>
        {error?.message && <div className="text-red my-2 p-2 border border-redLight text-sm">{error.message}</div>}
        <div className="flex gap-2">
          <Button disabled={loading} onClick={onCancel} type="button">
            Cancelar
          </Button>
          <Button disabled={loading} primary type="submit">
            Guardar
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default Edit
